import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Button, Flex, Grid, Group, ScrollArea, Stepper, Text, Tooltip } from "@mantine/core";
import t from "../../stores/language";
import { useParams } from "react-router-dom";
import InputSelector from "./input-selector";
import ResultView, { sortResults } from "./result-view";
import { useStyles } from "./formula-builder";
import { useQuery, useStore } from "../../stores/store";
import { FormulaElement, FormulaResult, QueryKeys } from "../../models/models";
import theme from "../../stores/theme";
import { getSortedData } from "./configure-formula";
import ReportSave, { ReportSaveHandle, defaultReport } from "./reports/report-save";
import { usePDF } from "react-to-pdf";
import ExportResultView from "../export-pdf/export-result-view";
import { Icon } from "../../custom-components/icons";
import { faDownload } from "@fortawesome/pro-duotone-svg-icons";

export const getDetailedResult = (
  orderedData: {
    [key: string]: string | number | null;
  }[],
  selectedFormulas: FormulaElement[]
) => {
  const detailedResult = orderedData.filter((result) => {
    return selectedFormulas.some((formula) => formula.name === result["Renewal Steps/Components"]);
  });
  return detailedResult;
};

const ModuleResultViewer = observer(function ModuleResultViewer() {
  const { id } = useParams();
  const [active, setActive] = useState(0);
  const { store, authStore } = useStore();
  const inputElements = store.GetInputByModule(id ?? "");
  const selectorValue = store.selectedInputs;
  const reportSaveRef = useRef<ReportSaveHandle>(null);
  const moduleAssumptions = store.GetAssumptionsByModuleId(id ?? "0");
  const formulaElements = store.GetFormulasByModule(id ?? "");
  const [calculatedFormulaResult, setCalculatedFormulaResult] = useState<{ [key: string]: string | number | null }[]>([]);
  const module = store.GetModuleById(id ?? "0");
  const formulaElementsOrder = (module?.moduleConfig?.formulaElementsOrder ?? []).map((ie) => ie.toString());
  const orderedFormulaElements = formulaElementsOrder.length > 0 ? (getSortedData(formulaElementsOrder, formulaElements) as FormulaElement[]) : formulaElements;
  const { classes } = useStyles({ resultErrors: store.resultErrors.length > 0, calculatedFormulaResult: calculatedFormulaResult.length });
  const orderedData = formulaElementsOrder.length > 0 ? sortResults(store.results, orderedFormulaElements) : store.results;
  const formulasToShowInDetailedResult = orderedFormulaElements.filter((formula) => formula.formulaConfig?.hideInDetailResults != true);
  const formulasToCalculate = orderedFormulaElements.filter((formula) => formula.formulaConfig?.showInCalculate == true);

  const nonCommentsGroups = inputElements.filter((input) => input.inputConfig?.group !== "Comments");
  const commentsGroups = inputElements.filter((input) => input.inputConfig?.group === "Comments");

  const { toPDF, targetRef } = usePDF({
    filename: module ? `${module.name.toLowerCase()}_result.pdf` : "result.pdf",
    page: {
      margin: 5,
    },
  });

  const { data: user } = useQuery({
    queryKey: [QueryKeys.User],
    queryFn: () => {
      return authStore.GetUserById(authStore.user?.profile.sub ?? "1");
    },
  });
  useEffect(() => {
    if (store.inputElements.length === 0 || id == null) return;
    const defaultValues: { [key: string]: string | number | null } = {};
    const inputs = store.GetInputByModule(id);
    inputs.forEach((i) => {
      defaultValues[i.id] = i.default != null && i.default != "" ? i.default : i.type === "Number Input" ? 0 : "";
    });
    store.UpdateSelectedInputs(defaultValues);
    setActive(0);
  }, [id, store.inputElements.length]);

  useEffect(() => {
    if ((active == 1 && moduleAssumptions != "") || (moduleAssumptions === "" && active == 0)) {
      if (store.results.length > 0) store.SetResults([]);
      if (store.resultErrors.length > 0) store.SetErrors([]);
      if (Object.keys(store.errorMessages)?.length > 0) store.SetErrorMessages({});
      setCalculatedFormulaResult([]);
    }
  }, [active, id]);
  const refresh = async () => {
    await store.EvaluateFormulas(store.selectedInputs ?? {}, id ?? "0").then((data: FormulaResult | null) => {
      if (data == null) return;
      store.SetResults(data?.result ?? []);
      store.SetErrors(data?.errors ?? []);
    });
  };
  const moduleActions = async (action: string) => {
    switch (action) {
      case "Save": {
        reportSaveRef.current?.show({
          ...defaultReport,
          moduleId: parseInt(id?.toString() ?? "0"),
          selectedInputs: store.selectedInputs,
          formulasResult: {
            detailedResult: getDetailedResult(orderedData, formulasToShowInDetailedResult),
            calculatedResult: calculatedFormulaResult,
          },
        });
        break;
      }
      case "Calculate": {
        if (store.validateInputs(store.selectedInputs ?? {})) {
          if (formulasToCalculate.length === 0) {
            setCalculatedFormulaResult([]);
          } else {
            await refresh();
            calculateSelctedFormulaResult();
          }
        }
        break;
      }
      case "View Details": {
        if (store.validateInputs(store.selectedInputs ?? {})) {
          await refresh();
          moduleAssumptions ? setActive(active != 2 ? active + 1 : active) : setActive(active != 1 ? active + 1 : active);
        }
        break;
      }
      case "Next":
        setActive(active != 1 ? active + 1 : active);
        break;
      case "Back":
        setActive(active != 0 ? active - 1 : active);
        break;
    }
  };

  const calculateSelctedFormulaResult = () => {
    if (store.resultErrors.length == 0 && store.results.length > 0) {
      const filteredResult = store.results.filter((result) => {
        return formulasToCalculate.some((formula) => formula.name === result["Renewal Steps/Components"]);
      });
      let calculatedResult = [];
      if (filteredResult.length > 0) {
        calculatedResult = sortResults(filteredResult, orderedFormulaElements).map((r) => {
          return {
            "Renewal Steps/Components": r["Renewal Steps/Components"]?.toString() ?? "",
            Cost: r["Cost"],
          };
        });
      } else {
        calculatedResult = formulasToCalculate.map((formula) => {
          return {
            "Renewal Steps/Components": formula.name,
            Cost: 0,
          };
        });
      }
      setCalculatedFormulaResult(calculatedResult);
    } else {
      setCalculatedFormulaResult([]);
    }
  };
  return (
    <div className={classes.container}>
      {id != null ? (
        <>
          <Stepper className={classes.stepperContainer} active={active} onStepClick={setActive} breakpoint="sm">
            {moduleAssumptions ? (
              <Stepper.Step label={t("Standard Assumptions")} description={t("Standard Assumptions")}>
                <div className={classes.steps}>
                  <ScrollArea offsetScrollbars style={{ height: "100%" }}>
                    <div dangerouslySetInnerHTML={{ __html: moduleAssumptions }} />
                  </ScrollArea>
                </div>
              </Stepper.Step>
            ) : null}
            <Stepper.Step label={t("Enter Inputs")} description={t("Enter required inputs")}>
              <Flex className={classes.steps} direction="column">
                <ScrollArea style={{ height: "100%", marginTop: theme.other?.arc.margins[2] }} offsetScrollbars>
                  <InputSelector span={4} />
                </ScrollArea>
                {calculatedFormulaResult.length > 0 || store.resultErrors.length > 0 || formulasToCalculate.length === 0 ? (
                  <div className={classes.calculationResultContainer}>
                    {store.resultErrors.length > 0 ? (
                      <Text align="center">{t("There is error in calculated result. Please contact admin")}</Text>
                    ) : formulasToCalculate.length === 0 ? (
                      <Text align="center">{t("There are no formulas selected to calculate. Please contact admin")}</Text>
                    ) : (
                      <ScrollArea offsetScrollbars style={{ height: "100%" }}>
                        <Group className={classes.calculationResult}>
                          {calculatedFormulaResult.map((r, i) => (
                            <Flex key={i} className={classes.calculatedResultItems}>
                              <Text align="center">{r["Renewal Steps/Components"]}</Text>
                              <Text weight={"bold"} align="center">
                                {r["Cost"]?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? ""}
                              </Text>
                            </Flex>
                          ))}
                        </Group>
                      </ScrollArea>
                    )}
                  </div>
                ) : null}
              </Flex>
            </Stepper.Step>
            {user?.userType !== 2 ? (
              <Stepper.Step label={t("Result")} description={t("View Result")}>
                <div className={classes.steps} style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ position: "absolute", left: "-9999px" }}>
                    <ExportResultView ref={targetRef} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm, marginTop: "2px" }}>Entered Inputs</div>
                    <Tooltip label={t(`Download ${module?.name ?? ""} Result Data`)} withArrow withinPortal>
                      <Button size="xs" variant="subtle" compact className={classes.iconButton} onClick={() => toPDF()}>
                        <Icon icon={faDownload} size="xl" />
                      </Button>
                    </Tooltip>
                  </div>
                  <Grid
                    style={{
                      marginBottom: theme.other?.arc.margins[1],
                      marginTop: theme.other?.arc.margins[0],
                      marginLeft: theme.other?.arc.margins[0],
                      marginRight: theme.other?.arc.margins[0],
                      padding: theme.other?.arc.paddings[1],
                      border: `1px solid ${theme.colors?.gray?.[3] ?? "#e2e2e2"}`,
                      maxHeight: 85,
                      overflowY: "auto",
                      flexShrink: 0,
                    }}
                  >
                    {nonCommentsGroups.map((input) => (
                      <Grid.Col span={4} key={input.id} style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <span style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm }}>{input.name} - </span>
                        <span style={{ marginLeft: 2, fontSize: theme.fontSizes?.sm }}>{selectorValue && selectorValue[input.id]}</span>
                      </Grid.Col>
                    ))}
                  </Grid>
                  <div style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm, marginTop: "4px" }}>Comments</div>
                  <Grid
                    style={{
                      marginBottom: theme.other?.arc.margins[1],
                      marginTop: theme.other?.arc.margins[0],
                      marginLeft: theme.other?.arc.margins[0],
                      marginRight: theme.other?.arc.margins[0],
                      padding: theme.other?.arc.paddings[1],
                      border: `1px solid ${theme.colors?.gray?.[3] ?? "#e2e2e2"}`,
                      maxHeight: 85,
                      overflowY: "auto",
                      flexShrink: 0,
                    }}
                  >
                    {commentsGroups.map((input) => (
                      <Grid.Col span={4} key={input.id} style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <span style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm }}>{input.name} - </span>
                        <span style={{ marginLeft: 2, fontSize: theme.fontSizes?.sm }}>{selectorValue && selectorValue[input.id]}</span>
                      </Grid.Col>
                    ))}
                  </Grid>
                  <ResultView />
                </div>
              </Stepper.Step>
            ) : null}
            <Stepper.Completed>{t("Completed, click back button to get to previous step")}</Stepper.Completed>
          </Stepper>
          <Group position="center" mt={10}>
            {active > 0 ? (
              <Button variant="default" onClick={() => moduleActions("Back")}>
                {t("Back")}
              </Button>
            ) : null}
            {((moduleAssumptions === "" && active != 1) || (moduleAssumptions != "" && active != 2)) && formulasToCalculate.length != 0 && (
              <Button onClick={() => moduleActions(active == 0 && moduleAssumptions ? "Next" : "Calculate")}>
                {active == 0 && moduleAssumptions ? "Next Step" : "Calculate"}
              </Button>
            )}
            {((moduleAssumptions === "" && active == 0) || (moduleAssumptions != "" && active == 1)) && calculatedFormulaResult.length > 0 ? (
              <Button onClick={() => moduleActions("Save")}>{t("Save")}</Button>
            ) : null}
            {user?.userType !== 2 && ((moduleAssumptions === "" && active == 0) || (moduleAssumptions != "" && active == 1)) ? (
              <Button onClick={() => moduleActions("View Details")}>{t("View Details")}</Button>
            ) : null}
          </Group>
        </>
      ) : (
        <div className={classes.title}>{t("Please select one module to enter inputs and view results")}</div>
      )}
      <ReportSave ref={reportSaveRef} />
    </div>
  );
});
export default ModuleResultViewer;
